import React from "react";
import styled from "styled-components";
import { Pie, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend);

const moneyBRL = (v) =>
  Number(v || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const CenterTextPlugin = {
  id: "centerTextPlugin",
  afterDraw(chart) {
    const { ctx, chartArea, config } = chart;
    if (!chartArea) return;

    const pluginOptions = config?.options?.plugins?.centerTextPlugin || {};
    const percent = Number(pluginOptions.percent || 0);
    const value = pluginOptions.value || "";

    const x = (chartArea.left + chartArea.right) / 2;
    const y = (chartArea.top + chartArea.bottom) / 2;

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    ctx.fillStyle = "#000";
    ctx.font = "700 22px Arial";
    ctx.fillText(`${percent.toFixed(0)}%`, x, y - 10);

    ctx.fillStyle = "#666";
    ctx.font = "12px Arial";
    ctx.fillText(value, x, y + 18);

    ctx.restore();
  },
};

const PieLabelsPlugin = {
  id: "pieLabelsPlugin",
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const dataset = chart.data.datasets[0];
    const data = dataset?.data || [];
    const total = data.reduce((acc, value) => acc + Number(value || 0), 0);

    if (!total) return;

    const meta = chart.getDatasetMeta(0);

    ctx.save();
    ctx.fillStyle = "#fff";
    ctx.font = "700 12px Arial";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    meta.data.forEach((element, index) => {
      const value = Number(data[index] || 0);
      if (value <= 0) return;

      const percentage = ((value / total) * 100).toFixed(0) + "%";

      const position = element.tooltipPosition();
      ctx.fillText(percentage, position.x, position.y);
    });

    ctx.restore();
  },
};

const DoughnutMeta = ({
  metaDiariaCalculada = 0,
  valorEmitidoHoje = 0,
  valorRestanteHoje = 0,
}) => {
  const totalMeta = Number(metaDiariaCalculada || 0);
  const emitido = Number(valorEmitidoHoje || 0);
  const restante = Number(valorRestanteHoje || 0);

  const percentual = totalMeta > 0 ? (emitido / totalMeta) * 100 : 0;
  const percentualExibicao = percentual > 100 ? 100 : percentual;
  const corPrincipal = percentual >= 100 ? "#52c41a" : "#1677ff";

  const data = {
    labels: ["Emitido hoje", "Restante"],
    datasets: [
      {
        data: [emitido, restante],
        backgroundColor: [corPrincipal, "#484848"],
        borderWidth: 0,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "70%",
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || "";
            const value = context.raw || 0;
            return `${label}: ${moneyBRL(value)}`;
          },
        },
      },
      centerTextPlugin: {
        percent: percentualExibicao,
        value: moneyBRL(emitido),
      },
    },
  };

  return (
    <ChartCard>
      <ChartTitle>Progresso da Meta</ChartTitle>

      <ChartArea>
        <Doughnut
          data={data}
          options={options}
          plugins={[CenterTextPlugin]}
        />
      </ChartArea>

      <LegendRow>
        <LegendItem>
          <LegendDot style={{ background: corPrincipal }} />
          Emitido: {moneyBRL(emitido)}
        </LegendItem>

        <LegendItem>
          <LegendDot style={{ background: "#484848" }} />
          Restante: {moneyBRL(restante)}
        </LegendItem>
      </LegendRow>
    </ChartCard>
  );
};

const PieOrigem = ({
  valorLojaHoje = 0,
  valorRoboHoje = 0,
  valorRestanteHoje = 0,
}) => {
  const robo = Number(valorRoboHoje || 0);
  const loja = Number(valorLojaHoje || 0);
  const restante = Number(valorRestanteHoje || 0);

  const data = {
    labels: ["Robô hoje", "Loja hoje", "Restante hoje"],
    datasets: [
      {
        data: [robo, loja, restante],
        backgroundColor: [
  "#0b3d91", // azul escuro (Robô)
  "#1677ff", // azul padrão (Loja)
  "#91caff", // azul claro (Restante)
],
        borderColor: "#ffffff",
        borderWidth: 2,
        hoverOffset: 8,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 10,
          padding: 16,
          font: {
            size: 12,
          },
          color: "#333",
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || "";
            const value = context.raw || 0;
            return `${label}: ${moneyBRL(value)}`;
          },
        },
      },
    },
  };

  return (
    <ChartCard>
      <ChartTitle>Robô x Loja x Restante</ChartTitle>

      <ChartArea>
        <Pie
          data={data}
          options={options}
          plugins={[PieLabelsPlugin]}
        />
      </ChartArea>
    </ChartCard>
  );
};

const MetaDoDia = ({
  metaDiariaCalculada = 0,
  valorEmitidoHoje = 0,
  valorRestanteHoje = 0,
  valorLojaHoje = 0,
  valorRoboHoje = 0,
}) => {
  return (
    <Container>
      <Header>Meta do Dia</Header>

      <ChartsRow>
        <DoughnutMeta
          metaDiariaCalculada={metaDiariaCalculada}
          valorEmitidoHoje={valorEmitidoHoje}
          valorRestanteHoje={valorRestanteHoje}
        />

        <PieOrigem
          valorLojaHoje={valorLojaHoje}
          valorRoboHoje={valorRoboHoje}
          valorRestanteHoje={valorRestanteHoje}
        />
      </ChartsRow>

      <ResumoRow>
        <ResumoItem>
          <ResumoLabel>Meta diária</ResumoLabel>
          <ResumoValue>{moneyBRL(metaDiariaCalculada)}</ResumoValue>
        </ResumoItem>

        <ResumoItem>
          <ResumoLabel>Emitido hoje</ResumoLabel>
          <ResumoValue>{moneyBRL(valorEmitidoHoje)}</ResumoValue>
        </ResumoItem>

        <ResumoItem>
          <ResumoLabel>Restante hoje</ResumoLabel>
          <ResumoValue>{moneyBRL(valorRestanteHoje)}</ResumoValue>
        </ResumoItem>

        <ResumoItem>
          <ResumoLabel>Loja hoje</ResumoLabel>
          <ResumoValue>{moneyBRL(valorLojaHoje)}</ResumoValue>
        </ResumoItem>

        <ResumoItem>
          <ResumoLabel>Robô hoje</ResumoLabel>
          <ResumoValue>{moneyBRL(valorRoboHoje)}</ResumoValue>
        </ResumoItem>
      </ResumoRow>
    </Container>
  );
};

export default MetaDoDia;

const Container = styled.div`
  margin-top: 20px;
  padding: 18px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.06);
`;

const Header = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #000;
  margin-bottom: 18px;
  text-align: center;
`;

const ChartsRow = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  justify-content: center;
`;

const ChartCard = styled.div`
  width: 340px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 14px;
  padding: 14px;
`;

const ChartTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #111;
  text-align: center;
  margin-bottom: 10px;
`;

const ChartArea = styled.div`
  height: 260px;
`;

const LegendRow = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #333;
  font-size: 14px;
`;

const LegendDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
`;

const ResumoRow = styled.div`
  margin-top: 18px;
  display: flex;
  gap: 14px;
  justify-content: center;
  flex-wrap: wrap;
`;

const ResumoItem = styled.div`
  min-width: 160px;
  background: #fafafa;
  border-radius: 12px;
  padding: 12px 14px;
  border: 1px solid #f0f0f0;
  text-align: center;
`;

const ResumoLabel = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
`;

const ResumoValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #111;
`;